import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { UsableError } from 'src/services/error-parsing/error-parsing.types';
import {
  buildReportMetrics,
  buildReportingApiFilters,
  computePriorDateRange,
  rowsToAppTable,
  rowsToBreakdown,
  rowsToConversionsByBeacon,
  rowsToDomainTable,
  rowsToTimeSeries,
  rowsToZipcodeTable,
} from './reporting-api-v1.adapters';
import {
  fetchReportingApiFlights,
  fetchReportingApiMetaAccounts,
  fetchReportingApiRows,
  ReportingApiV1RequestError,
} from './reporting-api-v1.base';
import {
  REPORTING_API_V1_BASE_METRICS,
  REPORTING_API_V1_COMPLETION_METRICS,
  ReportingApiMetaAccount,
  ReportingApiV1BreakdownRow,
  ReportingApiV1ConversionsByBeaconRow,
  ReportingApiV1FlightsRequest,
  ReportingApiV1ReportMetrics,
  ReportingApiV1SelectionFilters,
  ReportingApiV1TablePoint,
  ReportingApiV1TimeSeriesPoint,
} from './reporting-api-v1.types';
import { normaliseFlightRow } from 'src/redux/admin/reporting/flight-row.utils';
import { FlightReportRow } from 'src/redux/admin/reporting/reporting.types';

const metricsList = [
  ...REPORTING_API_V1_BASE_METRICS,
  'reach',
  'results',
  ...REPORTING_API_V1_COMPLETION_METRICS,
];

const timeSeriesMetricsList = [
  ...REPORTING_API_V1_BASE_METRICS,
  ...REPORTING_API_V1_COMPLETION_METRICS,
];

const toQueryError = (error: unknown): UsableError => {
  const err = error as ReportingApiV1RequestError;
  return {
    code: err.status ?? 13,
    message: err.message || 'Reporting API request failed',
    httpStatus: err.status,
    responseData: err.code ? { code: err.code } : undefined,
  };
};

const reportingApiV1Endpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getReportingApiV1MetaAccounts: builder.query<
    ReportingApiMetaAccount[],
    { extClientId?: string } | void
    >({
      async queryFn(arg) {
        try {
          const data = await fetchReportingApiMetaAccounts(arg?.extClientId);
          return { data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    getReportingApiV1Metrics: builder.mutation<
    ReportingApiV1ReportMetrics,
    ReportingApiV1SelectionFilters
    >({
      async queryFn(selection) {
        try {
          const filters = buildReportingApiFilters(selection);
          const pastRange = computePriorDateRange(selection.startDate, selection.endDate);
          const [currentRows, pastRows] = await Promise.all([
            fetchReportingApiRows({
              dataset: 'performance',
              dimensions: [],
              metrics: metricsList,
              filters,
              start_date: selection.startDate,
              end_date: selection.endDate,
              limit: 1,
              extClientId: selection.extClientId,
            }),
            fetchReportingApiRows({
              dataset: 'performance',
              dimensions: [],
              metrics: metricsList,
              filters,
              start_date: pastRange.startDate,
              end_date: pastRange.endDate,
              limit: 1,
              extClientId: selection.extClientId,
            }),
          ]);
          return {
            data: buildReportMetrics(
              currentRows[0],
              pastRows[0],
              { startDate: selection.startDate, endDate: selection.endDate },
              pastRange
            ),
          };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    getReportingApiV1TimeSeries: builder.mutation<
    ReportingApiV1TimeSeriesPoint[],
    ReportingApiV1SelectionFilters
    >({
      async queryFn(selection) {
        try {
          const rows = await fetchReportingApiRows({
            dataset: 'performance',
            dimensions: ['date'],
            metrics: timeSeriesMetricsList,
            filters: buildReportingApiFilters(selection),
            start_date: selection.startDate,
            end_date: selection.endDate,
            sort: ['date:asc'],
            limit: 1000,
            extClientId: selection.extClientId,
          });
          return { data: rowsToTimeSeries(rows) };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    getReportingApiV1Geo: builder.mutation<
    ReportingApiV1TablePoint[],
    ReportingApiV1SelectionFilters
    >({
      async queryFn(selection) {
        try {
          const rows = await fetchReportingApiRows({
            dataset: 'geo',
            dimensions: ['zipcode'],
            metrics: [...REPORTING_API_V1_BASE_METRICS],
            filters: buildReportingApiFilters(selection),
            start_date: selection.startDate,
            end_date: selection.endDate,
            sort: ['impressions:desc'],
            limit: 50,
            extClientId: selection.extClientId,
          });
          return { data: rowsToZipcodeTable(rows) };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    getReportingApiV1Domain: builder.mutation<
    ReportingApiV1TablePoint[],
    ReportingApiV1SelectionFilters
    >({
      async queryFn(selection) {
        try {
          const rows = await fetchReportingApiRows({
            dataset: 'domain',
            dimensions: ['domain'],
            metrics: [...REPORTING_API_V1_BASE_METRICS],
            filters: buildReportingApiFilters(selection),
            start_date: selection.startDate,
            end_date: selection.endDate,
            sort: ['impressions:desc'],
            limit: 20,
            extClientId: selection.extClientId,
          });
          return { data: rowsToDomainTable(rows) };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    getReportingApiV1App: builder.mutation<
    ReportingApiV1TablePoint[],
    ReportingApiV1SelectionFilters
    >({
      async queryFn(selection) {
        try {
          const rows = await fetchReportingApiRows({
            dataset: 'domain',
            dimensions: ['app_id', 'app_name'],
            metrics: [...REPORTING_API_V1_BASE_METRICS],
            filters: buildReportingApiFilters(selection),
            start_date: selection.startDate,
            end_date: selection.endDate,
            sort: ['impressions:desc'],
            limit: 20,
            extClientId: selection.extClientId,
          });
          return { data: rowsToAppTable(rows) };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    getReportingApiV1Breakdown: builder.mutation<
    ReportingApiV1BreakdownRow[],
    ReportingApiV1SelectionFilters
    >({
      async queryFn(selection) {
        try {
          const rows = await fetchReportingApiRows({
            dataset: 'performance',
            dimensions: ['campaign_id', 'campaign_name'],
            metrics: [
              ...REPORTING_API_V1_BASE_METRICS,
              'video_starts',
              'video_completions',
            ],
            filters: buildReportingApiFilters(selection),
            start_date: selection.startDate,
            end_date: selection.endDate,
            sort: ['impressions:desc'],
            limit: 1000,
            extClientId: selection.extClientId,
          });
          return { data: rowsToBreakdown(rows) };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    getReportingApiV1ConversionsByBeacon: builder.mutation<
    ReportingApiV1ConversionsByBeaconRow[],
    ReportingApiV1SelectionFilters
    >({
      async queryFn(selection) {
        try {
          const filters = {
            ...buildReportingApiFilters(selection),
            attribution_window: ['platform_default'],
          };
          const rows = await fetchReportingApiRows({
            dataset: 'conversions',
            dimensions: ['conversion_id', 'conversion_name', 'conversion_type'],
            metrics: ['conversions', 'conversion_value', 'view_through_conversions'],
            filters,
            start_date: selection.startDate,
            end_date: selection.endDate,
            sort: ['conversions:desc'],
            limit: 1000,
            extClientId: selection.extClientId,
          });
          return { data: rowsToConversionsByBeacon(rows) };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    getReportingApiV1Flights: builder.mutation<
    FlightReportRow[],
    ReportingApiV1FlightsRequest
    >({
      async queryFn(selection) {
        try {
          const rows = await fetchReportingApiFlights(selection);
          return {
            data: rows.map((row) => normaliseFlightRow(row as FlightReportRow)),
          };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetReportingApiV1MetaAccountsQuery,
  useGetReportingApiV1MetricsMutation,
  useGetReportingApiV1TimeSeriesMutation,
  useGetReportingApiV1GeoMutation,
  useGetReportingApiV1DomainMutation,
  useGetReportingApiV1AppMutation,
  useGetReportingApiV1BreakdownMutation,
  useGetReportingApiV1ConversionsByBeaconMutation,
  useGetReportingApiV1FlightsMutation,
} = reportingApiV1Endpoints;
